import { useState } from "react";

import { Check, ChevronLeft, CirclePlus, Info } from "lucide-react";
import { useDispatch } from "react-redux";

import { DocxPreview } from "@/components/FilePreview/DocxPreview";
import { useAppForm } from "@/components/Form";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { FILE_EXTENSIONS, FILE_MIME_TYPES, FILE_SIZES } from "@/constants/file";
import {
  SERVICE_TYPES,
  getServiceTypeLabel,
  getTemplateNameForServiceType,
} from "@/constants/service-types";
import {
  useCreateCase,
  useExtractExamineeAndClaimNumber,
} from "@/hooks/mutations/use-cases";
import { useDoctorOptions } from "@/hooks/queries/use-doctor-options";
import { useIsAdmin } from "@/hooks/queries/use-me";
import { useTemplates } from "@/hooks/queries/use-templates";
import { setMessage } from "@/redux/notification";
import { formatFileSize } from "@/utils/fileFormat";

import { ReactComponent as GreenCircleTick } from "@assets/svg/green-circle-tick.svg";
import { ReactComponent as LoadingIcon } from "@assets/svg/loading-spinner.svg";
import { ReactComponent as RedCircleTick } from "@assets/svg/red-circle-tick.svg";

import { DOCTOR_ACCESS_COPY } from "@constants/doctor-access";

import { ParseStateChip, parseStateFrom } from "./ParseStateChip";
import { TemplatePreview } from "./TemplatePreview";

type CheckExtractExamineeAndClaimNumberResponse = {
  claim_number: string;
  examinee_name: string;
};

export const CreateCaseModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [fieldsEnabled, setFieldsEnabled] = useState(false);
  const [extractLoading, setExtractLoading] = useState(false);
  const [extractionFailed, setExtractionFailed] = useState(false);
  const [examineeName, setExamineeName] = useState("");
  const [claimNumber, setClaimNumber] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Preview states
  const [showPreview, setShowPreview] = useState(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [previewType, setPreviewType] = useState<"doc" | "unsupported">(
    "unsupported"
  );
  const [previewLoading, setPreviewLoading] = useState(false);

  const dispatch = useDispatch();
  const createCase = useCreateCase();
  const extractExamineeAndClaimNumber = useExtractExamineeAndClaimNumber();

  const { data: templates = [], isLoading: isTemplatesLoading } = useTemplates();

  const templateOptions = templates.map((template) => ({
    value: template.id,
    label: template.name,
  }));

  // MDRS-234: the "create for doctor" picker is admin-only. `enabled: isAdmin`
  // keeps non-admins from calling the admin users endpoint at all.
  const isAdmin = useIsAdmin();
  // Keyed on email, not id: POST /cases/ resolves the doctor by `doctor_email`.
  const { options: assignableDoctors, isLoading: isDoctorsLoading } =
    useDoctorOptions();
  const doctorOptions = assignableDoctors.map((d) => ({
    value: d.email,
    label: `${d.label} (${d.email})`,
  }));

  // MDRS-230: how much the extractor read, derived from what came back rather
  // than a new API field. `parseEdited` flips once the user corrects a value, so
  // the chip stops claiming the parser produced what's on screen.
  const [parseEdited, setParseEdited] = useState(false);
  const parseState = parseStateFrom({
    claimNumber,
    examineeName,
    edited: parseEdited,
  });

  const form = useAppForm({
    defaultValues: {
      template_id: "",
      file: null as File | null,
      claim_number: "",
      examinee_name: "",
      report_due_date: "",
      appointment_date: "",
      organisation: "",
      service_type: "",
      doctor_email: "",
    },
    onSubmit: async ({ value }) => {
      // Validate all fields are filled. service_type is required (MDRS-141): it
      // drives the template selection and populates case.service_type, which the
      // Assessment Scribe recorder keys off to resolve its section tabs.
      if (
        !value.service_type ||
        !value.template_id ||
        !value.file ||
        !value.claim_number ||
        !value.examinee_name
      ) {
        dispatch(
          setMessage({
            content: "Please fill in all required fields",
            type: "error",
          })
        );
        return;
      }

      // Show confirmation modal instead of submitting directly
      setShowConfirmation(true);
    },
  });

  // Actual form submission after confirmation
  const handleConfirmSubmit = async () => {
    try {
      const formData = new FormData();

      // Add extracted fields
      formData.append("claim_number", form.getFieldValue("claim_number") || "");
      formData.append("examinee_name", form.getFieldValue("examinee_name") || "");
      formData.append("template_id", form.getFieldValue("template_id") || "");
      if (form.getFieldValue("report_due_date")) {
        formData.append("report_due_date", form.getFieldValue("report_due_date"));
      }
      if (form.getFieldValue("appointment_date")) {
        formData.append("appointment_date", form.getFieldValue("appointment_date"));
      }
      if (form.getFieldValue("organisation")) {
        formData.append("organisation", form.getFieldValue("organisation"));
      }
      if (form.getFieldValue("service_type")) {
        formData.append("service_type", form.getFieldValue("service_type"));
      }
      // MDRS-234: only admins see the picker; the backend ignores it otherwise.
      if (form.getFieldValue("doctor_email")) {
        formData.append("doctor_email", form.getFieldValue("doctor_email"));
      }

      // Add the file
      if (form.getFieldValue("file")) {
        formData.append("file", form.getFieldValue("file") as File);
      }

      // Call the API
      await createCase.mutateAsync(formData);

      dispatch(
        setMessage({
          content: "Case created successfully",
          type: "success",
        })
      );

      // Reset form and close modal
      form.reset();
      setFieldsEnabled(false);
      setShowConfirmation(false);
      setIsOpen(false);
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to create case";
      dispatch(
        setMessage({
          content: errorMessage,
          type: "error",
        })
      );
    }
  };

  const handleGoBack = () => {
    setShowConfirmation(false);
  };

  const handleFileRemove = () => {
    // Reset all state
    setFieldsEnabled(false);
    setExtractionFailed(false);
    setExamineeName("");
    setClaimNumber("");
    setParseEdited(false);
    setExtractLoading(false);

    // Reset form values
    form.setFieldValue("file", null);
    form.setFieldValue("claim_number", "");
    form.setFieldValue("examinee_name", "");
    form.setFieldValue("template_id", "");
    form.setFieldValue("report_due_date", "");
    form.setFieldValue("appointment_date", "");
    form.setFieldValue("organisation", "");
    form.setFieldValue("service_type", "");
  };

  const handleModalOpenChange = (open: boolean) => {
    setIsOpen(open);

    // Reset all state when modal is closed
    if (!open) {
      handleFileRemove();

      // Clean up preview state
      setShowPreview(false);
      setShowConfirmation(false);
      setPreviewFile(null);
      if (previewUrl && previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl);
      }
      setPreviewUrl("");
      setPreviewType("unsupported");
      setPreviewLoading(false);
    }
  };

  // Handle file upload and extraction
  const handleFileChange = async (file: File | null) => {
    setFieldsEnabled(false);
    setExtractionFailed(false);
    form.setFieldValue("file", file);

    if (!file) {
      handleFileRemove();
      return;
    }

    setExtractLoading(true);
    try {
      const response = (await extractExamineeAndClaimNumber.mutateAsync(
        file
      )) as CheckExtractExamineeAndClaimNumberResponse;

      // Check if extraction was successful
      if (response && (response.claim_number || response.examinee_name)) {
        form.setFieldValue("claim_number", response.claim_number || "");
        form.setFieldValue("examinee_name", response.examinee_name || "");
        setClaimNumber(response.claim_number || "");
        setExamineeName(response.examinee_name || "");
        setFieldsEnabled(true);
        setExtractionFailed(false);
      } else {
        // Extraction failed - enable fields for manual input
        form.setFieldValue("claim_number", "");
        form.setFieldValue("examinee_name", "");
        setClaimNumber("");
        setExamineeName("");
        setFieldsEnabled(true);
        setExtractionFailed(true);
      }
    } catch (error: unknown) {
      // Extraction failed due to error - enable fields for manual input
      form.setFieldValue("claim_number", "");
      form.setFieldValue("examinee_name", "");
      setClaimNumber("");
      setExamineeName("");
      setFieldsEnabled(true);
      setExtractionFailed(true);

      const errorMessage =
        error instanceof Error ? error.message : "Failed to extract information";
      dispatch(
        setMessage({
          content: errorMessage,
          type: "error",
        })
      );
    } finally {
      setExtractLoading(false);
    }
  };

  const handleCancel = () => {
    form.reset();
    setFieldsEnabled(false);
    setExtractionFailed(false);
    setExamineeName("");
    setClaimNumber("");
    setParseEdited(false);
    setExtractLoading(false);
    setIsOpen(false);
  };

  const handlePreview = (file: File) => {
    setPreviewLoading(true);
    setPreviewFile(file);

    const fileExtension = file.name.split(".").pop()?.toLowerCase();
    const isWordDoc =
      (FILE_EXTENSIONS.WORD_DOCUMENTS as readonly string[]).includes(
        fileExtension || ""
      ) ||
      file.type?.includes("word") ||
      file.type?.includes("document");

    if (isWordDoc) {
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);
      setPreviewType("doc");
    } else {
      setPreviewType("unsupported");
    }

    setPreviewLoading(false);
    setShowPreview(true);
  };

  const handleBackFromPreview = () => {
    setShowPreview(false);
    setPreviewFile(null);
    if (previewUrl && previewUrl.startsWith("blob:")) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl("");
    setPreviewType("unsupported");
  };

  // Helper function to get selected template name
  const getSelectedTemplateName = () => {
    const selectedTemplate = templates.find(
      (t) => t.id === form.getFieldValue("template_id")
    );
    return selectedTemplate?.name || "";
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleModalOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm" className="space-x-2">
          <CirclePlus className="size-4" />
          Add Case
        </Button>
      </DialogTrigger>
      <DialogContent
        className={`flex max-h-[90vh] flex-col overflow-hidden rounded-2xl bg-background px-8 py-10 ${
          showPreview ? "min-w-[900px] max-w-4xl" : "min-w-[600px] max-w-lg"
        }`}
      >
        {showPreview ? (
          // Preview View
          <>
            <div className="mb-4 grid w-full grid-cols-[auto_1fr_auto] items-center gap-4 pr-8">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleBackFromPreview}
                className="flex items-center gap-2 text-blue-600"
              >
                <ChevronLeft className="h-4 w-4" />
                Back
              </Button>
              <DialogTitle className="min-w-0 truncate text-center text-xl font-semibold">
                {previewFile?.name || "Document Preview"}
              </DialogTitle>
              <DialogDescription className="sr-only">File preview</DialogDescription>
            </div>

            <div className="flex-1 overflow-y-auto" style={{ height: "70vh" }}>
              {previewLoading ? (
                <div className="flex h-full items-center justify-center">
                  <LoadingIcon className="size-8 animate-spin" />
                </div>
              ) : previewType === "doc" && previewUrl ? (
                <div className="h-full w-full">
                  <DocxPreview fileUrl={previewUrl} fitToWidth />
                </div>
              ) : (
                <div className="flex h-full items-center justify-center">
                  <div className="text-center">
                    <p className="text-lg font-medium text-foreground-muted">
                      File preview not available
                    </p>
                    <p className="text-sm text-foreground-muted">
                      Only Word documents (.doc, .docx) are supported for preview
                    </p>
                  </div>
                </div>
              )}
            </div>
          </>
        ) : showConfirmation ? (
          // Confirmation View
          <>
            <div className="space-y-6 py-4">
              <div className="flex justify-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-teal-100">
                  <Check className="h-8 w-8 text-success" />
                </div>
              </div>
              <DialogTitle className="text-center text-2xl font-semibold leading-relaxed">
                Confirm Case Details
              </DialogTitle>
              <DialogDescription className="text-center text-sm">
                You&apos;re about to create a new case using the following details.
                Please review and confirm before proceeding.
              </DialogDescription>

              <div className="space-y-3 rounded-xl bg-gray-50 p-4">
                <div className="flex justify-between">
                  <span className="font-medium">Template:</span>
                  <span>{getSelectedTemplateName()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Uploaded File:</span>
                  <span>
                    {form.getFieldValue("file")?.name} (
                    {formatFileSize(form.getFieldValue("file")?.size || 0)})
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Claim Number:</span>
                  <span>{form.getFieldValue("claim_number")}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Examinee Name:</span>
                  <span>{form.getFieldValue("examinee_name")}</span>
                </div>
                {form.getFieldValue("organisation") && (
                  <div className="flex justify-between">
                    <span className="font-medium">Organisation:</span>
                    <span>{form.getFieldValue("organisation")}</span>
                  </div>
                )}
                {form.getFieldValue("service_type") && (
                  <div className="flex justify-between">
                    <span className="font-medium">Service Type:</span>
                    <span>
                      {getServiceTypeLabel(form.getFieldValue("service_type")) ||
                        form.getFieldValue("service_type")}
                    </span>
                  </div>
                )}
                {form.getFieldValue("report_due_date") && (
                  <div className="flex justify-between">
                    <span className="font-medium">Report Due Date:</span>
                    <span>{form.getFieldValue("report_due_date")}</span>
                  </div>
                )}
                {form.getFieldValue("appointment_date") && (
                  <div className="flex justify-between">
                    <span className="font-medium">Appointment Date:</span>
                    <span>{form.getFieldValue("appointment_date")}</span>
                  </div>
                )}
              </div>

              <div className="flex justify-start px-4">
                <p className="text-sm text-foreground-muted">
                  Would you like to continue?
                </p>
              </div>
            </div>

            <DialogFooter className="flex justify-center gap-4">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleGoBack}
                className="px-6"
              >
                No, Go Back & Edit
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={handleConfirmSubmit}
                disabled={createCase.isPending}
                className="px-6"
              >
                {createCase.isPending ? "Creating..." : "Yes, Confirm & Create Case"}
              </Button>
            </DialogFooter>
          </>
        ) : (
          // Original Form View
          <>
            <DialogTitle className="text-2xl font-semibold leading-relaxed">
              Create New Case
            </DialogTitle>
            <DialogDescription className="sr-only">
              Upload a document and fill in case details to create a new case.
            </DialogDescription>

            <form.AppForm>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  form.handleSubmit();
                }}
                className="flex min-h-0 flex-1 flex-col overflow-hidden"
              >
                <div className="min-h-0 flex-1 space-y-4 overflow-y-auto">
                  <form.AppField name="file">
                    {(field) => (
                      <field.FileField
                        label={form.getFieldValue("file") ? "" : "Upload File"}
                        subLabel={
                          form.getFieldValue("file")
                            ? "Uploaded File (1)"
                            : `Upload a document (${FILE_EXTENSIONS.WORD_DOCUMENTS.map(
                                (ext) => `.${ext}`
                              ).join(", ")})`
                        }
                        required
                        maxSize={FILE_SIZES.MB_50}
                        allowedTypes={FILE_EXTENSIONS.WORD_DOCUMENTS.map(
                          (ext) => `.${ext}`
                        )}
                        accept={[...FILE_MIME_TYPES.WORD_DOCUMENTS]}
                        multiple={false}
                        onFilesSelected={(files) => handleFileChange(files[0])}
                        onFileRemove={() => handleFileRemove()}
                        onPreview={handlePreview}
                        existingFiles={
                          form.getFieldValue("file")
                            ? [form.getFieldValue("file") as File]
                            : undefined
                        }
                      />
                    )}
                  </form.AppField>

                  {/* Show extraction progress while processing */}
                  {extractLoading && <ExtractionProgressSection />}

                  {/* Show extracted fields only after extraction */}
                  {fieldsEnabled && (
                    <>
                      <div className="rounded-xl bg-background-white p-4">
                        <div className="grid grid-cols-2 gap-4">
                          <form.AppField name="claim_number">
                            {(field) => (
                              <field.InputField
                                label="Claim number"
                                placeholder="Claim number"
                                value={form.getFieldValue("claim_number")}
                                onChange={(e) => {
                                  setClaimNumber(e.target.value);
                                  setParseEdited(true);
                                  form.setFieldValue("claim_number", e.target.value);
                                }}
                                required
                                className={
                                  extractionFailed &&
                                  !form.getFieldValue("claim_number")
                                    ? "border-destructive focus:border-destructive focus:ring-destructive"
                                    : ""
                                }
                              />
                            )}
                          </form.AppField>
                          <form.AppField name="examinee_name">
                            {(field) => (
                              <field.InputField
                                label="Examinee name"
                                placeholder="Examinee name"
                                value={form.getFieldValue("examinee_name")}
                                onChange={(e) => {
                                  setExamineeName(e.target.value);
                                  setParseEdited(true);
                                  form.setFieldValue(
                                    "examinee_name",
                                    e.target.value
                                  );
                                }}
                                required
                                className={
                                  extractionFailed &&
                                  !form.getFieldValue("examinee_name")
                                    ? "border-destructive focus:border-destructive focus:ring-destructive"
                                    : ""
                                }
                              />
                            )}
                          </form.AppField>
                        </div>

                        {/* Conditional message based on extraction result */}
                        {/* MDRS-230: one parse-state chip replaces the three
                            success/failure banners — same information, and it
                            stays accurate once the user edits a value. */}
                        <div className="mt-3 flex items-center justify-between gap-3">
                          <p className="text-xs text-foreground-muted">
                            {parseState === "nothing"
                              ? "Add the claim number and examinee name manually."
                              : "Review the values read from the document and edit if needed."}
                          </p>
                          <ParseStateChip state={parseState} />
                        </div>

                        <div className="mt-4 space-y-4">
                          <form.AppField name="organisation">
                            {(field) => (
                              <field.InputField
                                label="Organisation"
                                placeholder="Organisation"
                                value={form.getFieldValue("organisation")}
                                onChange={(e) =>
                                  form.setFieldValue("organisation", e.target.value)
                                }
                              />
                            )}
                          </form.AppField>
                          <form.AppField name="service_type">
                            {(field) => (
                              <field.SelectField
                                label="Service type"
                                subLabel="Determines the report template and Assessment Scribe sections"
                                placeholder="Select a service type"
                                required
                                options={SERVICE_TYPES.map((s) => ({
                                  value: s.value,
                                  label: s.label,
                                }))}
                                onValueChange={(value) => {
                                  field.handleChange(value);
                                  // MDRS-141: picking a service auto-selects the
                                  // name-matching template. The template field
                                  // stays editable so General / custom templates
                                  // remain reachable by changing it afterwards.
                                  const templateName =
                                    getTemplateNameForServiceType(value);
                                  const match = templates.find(
                                    (t) => t.name === templateName
                                  );
                                  if (match) {
                                    form.setFieldValue("template_id", match.id);
                                  }
                                }}
                              />
                            )}
                          </form.AppField>
                          {/* MDRS-234: admins create cases on a doctor's behalf.
                              Assigned at creation (not later) because blob paths
                              key off the owner. Hidden for non-admins, whose
                              cases are always their own. */}
                          {isAdmin && (
                            <div className="space-y-2">
                              <form.AppField name="doctor_email">
                                {(field) => (
                                  <field.SelectField
                                    label={DOCTOR_ACCESS_COPY.doctorLabel}
                                    subLabel={DOCTOR_ACCESS_COPY.doctorHint}
                                    placeholder="Select a doctor"
                                    loading={isDoctorsLoading}
                                    options={doctorOptions}
                                    onValueChange={(value) =>
                                      field.handleChange(value)
                                    }
                                  />
                                )}
                              </form.AppField>
                              {/* MDRS-230: the release rule sits directly under the
                                  picker — new cases are never visible on create. */}
                              <p className="flex items-start gap-1.5 text-xs text-foreground-muted">
                                <Info className="mt-px size-3.5 flex-none" />
                                {DOCTOR_ACCESS_COPY.createHint}
                              </p>
                            </div>
                          )}
                          <div className="grid grid-cols-2 gap-4">
                            <form.AppField name="report_due_date">
                              {(field) => (
                                <field.DateField label="Report due date" />
                              )}
                            </form.AppField>
                            <form.AppField name="appointment_date">
                              {(field) => (
                                <field.DateField label="Appointment date" />
                              )}
                            </form.AppField>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-4 rounded-xl bg-background-white p-4">
                        <form.AppField name="template_id">
                          {(field) => (
                            <field.SelectField
                              label="Report template"
                              subLabel="Choose a document template to organize your files"
                              placeholder="Select a template"
                              required
                              options={templateOptions}
                              loading={isTemplatesLoading}
                            />
                          )}
                        </form.AppField>

                        <form.Subscribe
                          selector={(state) => state.values.template_id}
                        >
                          {(templateId) => (
                            <TemplatePreview templateId={templateId as string} />
                          )}
                        </form.Subscribe>
                      </div>
                    </>
                  )}
                </div>
                <DialogFooter className="mt-6">
                  <DialogClose asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleCancel}
                    >
                      Cancel
                    </Button>
                  </DialogClose>
                  <form.SubmitButton
                    size="sm"
                    disabled={
                      !fieldsEnabled || createCase.isPending || extractLoading
                    }
                  >
                    Create Case
                  </form.SubmitButton>
                </DialogFooter>
              </form>
            </form.AppForm>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const ExtractionProgressSection = () => (
  <div className="rounded-xl bg-accent-50 px-5 py-4">
    <div className="flex items-center space-x-5">
      <LoadingIcon className="size-5 animate-spin-ccw" />
      <div>
        <h4 className="text-sm font-medium text-accent-800">
          Extracting information
        </h4>
        <p className="text-xs text-accent-800">
          Analyzing your document to extract the claim number and examinee name.
        </p>
      </div>
    </div>
  </div>
);
